import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Building,
  ArrowLeft,
  FileText,
  Flag,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Search,
  User,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import { getFormattedDate } from "../../utils/formatDate";
import { formatHeight } from "../../utils/formatHeight";
import defaultAvatar from "../../utils/profile.png";
import { ATHLETE_ATTRIBUTES_DEFAULTS } from "../../models";
import ExerciseTrendWidget from "../dashboard/ExerciseTrendWidget";
import ExerciseSnapshotWidget from "../dashboard/ExerciseSnapshotWidget";
import ReportModal from "./ReportModal";
import ContactRequestModal from "./ContactRequestModal";
import { getScoutContactAction } from "../../utils/scoutContactAction";

const parseAttributes = (attributes) => {
  if (typeof attributes === "string") {
    try {
      return JSON.parse(attributes || "{}");
    } catch {
      return {};
    }
  }

  return attributes || {};
};

const normalizeRecord = (value) => {
  if (Array.isArray(value)) return value[0] || null;
  return value || null;
};

const heroStatValue = (value, suffix = "") =>
  value || value === 0 ? `${value}${suffix}` : "—";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getTrainingData = (metrics) => metrics?.training || { bySport: {}, lastSport: "" };

const getExerciseEntries = (trainingData) => {
  const bySport =
    trainingData?.bySport && typeof trainingData.bySport === "object"
      ? trainingData.bySport
      : {};

  const sports = Object.keys(bySport);
  if (sports.length === 0) return [];

  const selectedSport =
    trainingData?.lastSport && bySport[trainingData.lastSport]
      ? trainingData.lastSport
      : sports[0];

  const fields = bySport[selectedSport]?.fields || {};

  return Object.entries(fields)
    .map(([fieldKey, field]) => {
      const latestValue = toNumber(field?.latest?.value);
      if (latestValue === null) return null;

      return {
        key: fieldKey,
        label: field?.title || fieldKey,
        value: latestValue,
        unit: field?.unit || "",
        fullLabel: field?.title || fieldKey,
      };
    })
    .filter(Boolean)
    .slice(0, 6);
};

const FieldCard = ({ title, subtitle, value }) => (
  <div className="rounded-2xl border border-gray-100 bg-white px-5 py-4 shadow-sm">
    <p className="text-lg font-semibold text-gray-900">{title}</p>
    <p className="mt-1 text-sm font-medium text-gray-500">{subtitle}</p>
    <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
      {value || "—"}
    </div>
  </div>
);

const ContactRow = ({ icon: Icon, title, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 text-blue-950">
      <Icon size={18} />
    </div>
    <div>
      <p className="text-base font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-800">{value || "—"}</p>
    </div>
  </div>
);

const ExerciseComparisonChart = ({ data }) => {
  if (!data.length) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-gray-500">
        No hay registros numéricos de ejercicios.
      </div>
    );
  }

  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="label"
            stroke="#9ca3af"
            fontSize={10}
            tickLine={false}
            axisLine={false}
            interval={0}
          />
          <YAxis hide />
          <Tooltip
            formatter={(value, _name, payload) => [
              `${value}${payload?.payload?.unit ? ` ${payload.payload.unit}` : ""}`,
              payload?.payload?.fullLabel || "Valor",
            ]}
            contentStyle={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              border: "none",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              fontSize: "12px",
              fontWeight: "500",
            }}
          />
          <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const CONTACT_HINTS = {
  pending: "Te avisaremos en la app cuando el atleta responda.",
  guardian_pending: "Esperando la aprobación del tutor legal.",
  rejected: "Tu solicitud anterior fue rechazada. Puedes enviar una nueva.",
};

const CoachAthleteDetailPage = ({ athlete, contactRequest, onOpenThread, onBack }) => {
  const currentAthlete = athlete || {};

  const attributes = useMemo(
    () => ({
      ...ATHLETE_ATTRIBUTES_DEFAULTS,
      ...parseAttributes(currentAthlete.attributes),
    }),
    [currentAthlete.attributes],
  );

  const career = normalizeRecord(currentAthlete.athlete_career);
  const latestTracking = normalizeRecord(currentAthlete.athlete_tracking);
  const metrics = latestTracking?.metrics || {};
  const trainingData = getTrainingData(metrics);
  const exerciseEntries = useMemo(() => getExerciseEntries(trainingData), [trainingData]);

  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const closeContact = useCallback(() => setIsContactOpen(false), []);

  // Contact never leaves the app: request form → athlete answers in-app → chat.
  const contactAction = getScoutContactAction(contactRequest);
  const contactHint = CONTACT_HINTS[contactRequest?.status];
  const handleContact = () => {
    if (contactAction.kind === "message") onOpenThread?.(contactRequest);
    else if (contactAction.kind === "request") setIsContactOpen(true);
  };
  const mainPosition = attributes.main_position || currentAthlete.position || "—";
  const gender = attributes.gender || attributes.genero || "";
  const discipline = attributes.sport || currentAthlete.sport || "—";
  const level = career?.competition_level || currentAthlete.category || "—";
  const club = career?.current_club || currentAthlete.country || "—";
  const location = currentAthlete.country || attributes.country || "—";
  const phone = attributes.phone || currentAthlete.phone || "—";

  return (
    <main className="flex-1 overflow-y-auto bg-gray-50">
      <div className="px-6 py-6 md:px-10 md:py-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={onBack}
              className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-blue-950"
            >
              <ArrowLeft size={16} />
              Volver
            </button>
            <p className="text-lg font-light text-gray-800">
              {getFormattedDate()}
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-900">
              Bienvenida de vuelta, {currentAthlete.full_name?.split(" ")[0] || "Coach"}
            </h1>
          </div>

          <div className="hidden items-center gap-5 text-gray-900 lg:flex">
            <button type="button" className="hover:text-blue-950" aria-label="Buscar">
              <Search size={28} />
            </button>
            <button
              type="button"
              className="hover:text-blue-950"
              aria-label="Notificaciones"
            >
              <Bell size={28} />
            </button>
            <button type="button" className="hover:text-blue-950" aria-label="Perfil">
              <User size={28} />
            </button>
          </div>
        </div>

        <section className="mt-6 border border-gray-300 bg-white px-5 py-5 shadow-sm sm:mt-8 sm:px-6 sm:py-6">
          {/* Stacks below sm: identity on top, actions on their own row, so the
              two buttons never squeeze or overflow the name at phone width. */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-center gap-4 sm:gap-5">
              <img
                src={attributes.avatar_url || currentAthlete.avatar_url || defaultAvatar}
                alt={currentAthlete.full_name || "Atleta"}
                onError={(event) => {
                  event.currentTarget.src = defaultAvatar;
                }}
                className="h-16 w-16 flex-shrink-0 object-cover sm:h-20 sm:w-20"
              />

              <div className="min-w-0">
                <h2 className="break-words text-lg font-semibold text-gray-900 sm:text-xl">
                  {currentAthlete.full_name || "Sin nombre"}
                </h2>
                <p className="text-sm text-blue-950 sm:text-base">{level}</p>
                <p className="text-xs text-gray-700">
                  {heroStatValue(attributes.points)}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:items-end">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleContact}
                  disabled={contactAction.disabled}
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 whitespace-nowrap bg-blue-950 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:px-6 sm:text-xs sm:tracking-[0.3em]"
                >
                  {contactAction.kind === "message" && <MessageSquare size={14} />}
                  {contactAction.label}
                </button>
                <Link
                  to={`/scout/reportes/${currentAthlete.id}`}
                  className="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 border border-blue-950 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-950 transition-colors hover:bg-blue-50 sm:px-6 sm:text-xs sm:tracking-[0.3em]"
                >
                  <FileText size={14} aria-hidden="true" />
                  Reporte
                </Link>
                <button
                  type="button"
                  onClick={() => setIsReportOpen(true)}
                  className="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 border border-gray-300 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600 transition-colors hover:bg-gray-50 hover:text-red-600 sm:px-6 sm:text-xs sm:tracking-[0.3em]"
                >
                  <Flag size={14} />
                  Reportar
                </button>
              </div>
              {contactHint && (
                <p className="text-xs text-gray-500">{contactHint}</p>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4 border-t border-gray-200 pt-5">
            <div>
              <p className="text-xs font-semibold text-gray-500 sm:text-sm">Peso</p>
              <p className="text-base font-semibold text-gray-900 sm:text-lg">
                {heroStatValue(attributes.weight, " kg")}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 sm:text-sm">Altura</p>
              <p className="text-base font-semibold text-gray-900 sm:text-lg">
                {formatHeight(attributes.height)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 sm:text-sm">Edad</p>
              <p className="text-base font-semibold text-gray-900 sm:text-lg">
                {heroStatValue(attributes.age, " años")}
              </p>
            </div>
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:mt-8 sm:gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="order-2 space-y-6 sm:space-y-8 xl:order-1">
            <section className="border border-gray-200 bg-white p-5 shadow-sm sm:p-8">
              <h3 className="text-xl font-semibold text-gray-900">Mi información</h3>
              <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
                <ContactRow
                  icon={Mail}
                  title="Correo electrónico"
                  value={currentAthlete.email || "—"}
                />
                <ContactRow icon={Phone} title="Teléfono de Contacto" value={phone} />
                <ContactRow icon={Building} title="Organización/Club" value={club} />
                <ContactRow
                  icon={MapPin}
                  title="País de residencia"
                  value={location}
                />
              </div>
            </section>

            <section className="grid grid-cols-1 gap-8 xl:grid-cols-2">
              <ExerciseTrendWidget data={trainingData} />
              <ExerciseSnapshotWidget data={trainingData} />
            </section>

            {/* Comidas ocultas hasta reconstruir la estimación de calorías
                (docs/roadmap/meal-nutrition.md): solo mostraba calorías, casi
                siempre 0, contra una meta inventada de 1000 kcal. */}

            <section className="border border-gray-200 bg-white p-5 shadow-sm sm:p-8">
              <h3 className="text-xl font-semibold text-gray-900">
                Ejercicios recientes
              </h3>
              <div className="mt-6">
                <ExerciseComparisonChart data={exerciseEntries} />
              </div>
            </section>
          </div>

          <aside className="order-1 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:order-2 xl:grid-cols-1">
            <FieldCard
              title="Posición"
              subtitle="Posición o especialidad"
              value={mainPosition}
            />
            <FieldCard
              title="Género"
              subtitle="Género del deportista"
              value={gender || "No registrado"}
            />
            <FieldCard
              title="Altura"
              subtitle="Estatura del deportista"
              value={formatHeight(attributes.height, "No registrada")}
            />
            <FieldCard
              title="Deporte Principal"
              subtitle="Deporte que practica"
              value={discipline}
            />
            <FieldCard
              title="Nivel"
              subtitle="Nivel de competencia"
              value={level}
            />
          </aside>
        </div>
      </div>
      <ContactRequestModal
        isOpen={isContactOpen}
        onClose={closeContact}
        athlete={currentAthlete}
      />
      <ReportModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        reportedAthleteId={currentAthlete.id || currentAthlete.athlete_id || null}
        reportedScoutId={null}
        reportedName={currentAthlete.full_name || ""}
        context="athlete_detail"
      />
    </main>
  );
};

export default CoachAthleteDetailPage;
